import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  Output, SimpleChanges
} from '@angular/core';
import { StatisticFiltersWidgetService } from './statistic-filters-widget.service';
import {
  componentDestroyed,
  handleError,
  League, Playoff, StatisticFilters,
  Tournament, TournamentDivision,
  TournamentGroup,
  TournamentRound,
  TournamentSeason,
  UntilDestroy
} from '@mtgame/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ToastService } from '@mtgame/ui';
import { combineLatest } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ROUND_QUERY_PARAM, SEASON_QUERY_PARAM, TOURNAMENT_QUERY_PARAM } from '@config/routing-map';

@UntilDestroy
@Component({
  selector: 'mtg-statistic-filters-widget',
  templateUrl: './statistic-filters-widget.component.html',
  styleUrls: ['./statistic-filters-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [StatisticFiltersWidgetService],
})
export class StatisticFiltersWidgetComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  league: League;
  @Input()
  seasonId: number;
  @Input()
  tournamentId: number;
  @Input()
  roundId: number;
  @Input()
  teamId: number;
  @Input()
  leaguePlayerId: number;
  @Input()
  perGameFilterVisible = true;
  @Input()
  additionalFiltersVisible = true;
  @Input()
  lastRoundDefault = false;
  @Output()
  filters = new EventEmitter<Partial<StatisticFilters>>();

  loading = false;
  seasons: TournamentSeason[] = [];
  tournaments: Tournament[] = [];
  divisions: TournamentDivision[] = [];
  rounds: TournamentRound[] = [];
  groups: TournamentGroup[] = [];
  tours: number[] = [];
  playoffs: Playoff[] = [];
  playoffRounds: number[] = [];

  formFilters = new UntypedFormGroup({
    league_id: new UntypedFormControl(),
    tournament_season_id: new UntypedFormControl(),
    tournament_id: new UntypedFormControl(),
    tournament_round_id: new UntypedFormControl(),
    division_id: new UntypedFormControl(),
    tournament_group_id: new UntypedFormControl(),
    tournament_tour: new UntypedFormControl(),
    playoff_id: new UntypedFormControl(),
    playoff_round: new UntypedFormControl(),
    query: new UntypedFormControl(),
    per_game: new UntypedFormControl(),
  });
  searchControl = new UntypedFormControl();

  constructor(
    private router: Router,
    private statisticFiltersWidgetService: StatisticFiltersWidgetService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['teamId'] || changes['leaguePlayerId'] || changes['league']) {
      if (this.loading) {
        return;
      }
      this.loading = true;
      try {
        await this.statisticFiltersWidgetService.initialize(
          {
            leagueId: this.league.id,
            leaguePlayerId: this.leaguePlayerId,
            teamId: this.teamId
          },
          {
            league_id: this.league.id,
            tournament_season_id: this.seasonId,
            tournament_id: this.tournamentId,
            tournament_round_id: this.roundId,
            per_game: false,
          },
          this.additionalFiltersVisible,
          this.lastRoundDefault,
        );
      } catch (e) {
        console.error(e);
        handleError(e);
      } finally {
        this.loading = false;
        this.cdr.markForCheck();
      }
    }
  }

  async ngOnInit(): Promise<void> {
    combineLatest([
      this.statisticFiltersWidgetService.seasons$.pipe(takeUntil(componentDestroyed(this))),
      this.statisticFiltersWidgetService.tournaments$.pipe(takeUntil(componentDestroyed(this))),
      this.statisticFiltersWidgetService.rounds$.pipe(takeUntil(componentDestroyed(this))),
      this.statisticFiltersWidgetService.groups$.pipe(takeUntil(componentDestroyed(this))),
      this.statisticFiltersWidgetService.tours$.pipe(takeUntil(componentDestroyed(this))),
      this.statisticFiltersWidgetService.playoffs$.pipe(takeUntil(componentDestroyed(this))),
    ]).subscribe(([
      seasons, tournaments, rounds, groups, tours,
      playoffs
    ]) => {
      this.seasons = seasons;
      this.tournaments = tournaments;
      this.rounds = rounds;
      this.groups = groups;
      this.tours = tours;
      this.playoffs = playoffs;
      this.cdr.markForCheck();
    });

    combineLatest([
      this.statisticFiltersWidgetService.divisions$.pipe(takeUntil(componentDestroyed(this))),
      this.statisticFiltersWidgetService.playoffRounds$.pipe(takeUntil(componentDestroyed(this))),
    ]).subscribe(([divisions, playoffRounds]) => {
      this.divisions = divisions;
      this.playoffRounds = playoffRounds;
      this.cdr.markForCheck();
    });

    this.statisticFiltersWidgetService.filters$
      .pipe(takeUntil(componentDestroyed(this)), filter(item => Object.keys(item).length > 0))
      .subscribe(filters => {
        this.formFilters.patchValue(filters, {emitEvent: false});
        this.filters.emit(filters);
      });
    this.formFilters.valueChanges.subscribe(async value => {
      await this.statisticFiltersWidgetService.setFilters(value, true);
      this.router.navigate([], {
        queryParams: {
          [SEASON_QUERY_PARAM]: value.tournament_season_id || null,
          [TOURNAMENT_QUERY_PARAM]: value.tournament_id || null,
          [ROUND_QUERY_PARAM]: value.tournament_round_id || null,
        },
        queryParamsHandling: 'merge',
      });
    });

    this.searchControl.valueChanges.pipe(
      debounceTime(500),
    ).subscribe(value => this.formFilters.get('query').setValue(value));
  }

  ngOnDestroy() {
    this.statisticFiltersWidgetService.dispose();
  }
}
